@if (isDialog) {
<div class="fixed inset-0 bg-gray-700 bg-opacity-75 flex justify-center items-center z-50 no-scrollbar p-4 md:p-6"
    (click)="onClose()">
    <div class="flex flex-col w-full max-w-3xl bg-white rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto no-scrollbar p-4 md:p-6"
        (click)="$event.stopPropagation()">
        <ng-container *ngTemplateOutlet="noticeContent"></ng-container>
    </div>
</div>
} @else {
<div class="flex flex-wrap mt-8 mb-6 w-full">
    <ng-container *ngTemplateOutlet="noticeContent"></ng-container>
</div>
}

<ng-template #noticeContent>
    @if (isDialog) {
    <div class="flex flex-row justify-between items-center border-b pb-3 mb-4 w-full border-blueGray-100">
        <div class="flex flex-col gap-0.5 justify-start">
            <h1 class="font-bold text-xl text-blueGray-800">
                {{ notice ? notice.title : 'Notice Details' }}
            </h1>
            <p class="text-primary-300 text-xs font-semibold">
                {{ notice ? 'Notice Details' : 'Loading...' }}
            </p>
        </div>
        <btn-icon icon="fa fa-close" (click)="onClose()" class="text-primary-300 hover:text-primary-300"></btn-icon>
    </div>
    } @else {
    <div class="w-full flex flex-row justify-between mb-4 pb-2 border-b border-blueGray-100">
        <app-btn-back routeTo=".."></app-btn-back>
    </div>
    }

    <!-- Main Container -->

    <!-- Loading State -->
    @if (loading) {
    <div class="px-auto w-full flex flex-col justify-center notices-center">
        <div class="inline-block">
            <app-loading-dots color="bg-primary-500"></app-loading-dots>
        </div>
        <span class="text-sm text-blueGray-400 mt-2 w-full text-center">Loading notice details...</span>
    </div>
    }

    <!-- Content -->
    @if (!loading && notice) {
    <div class="flex flex-col gap-6 w-full md:px-0">

        @if (!isDialog) {
        <!-- Title Row -->
        <div class="border-b border-blueGray-100 w-full">
            <h1 class="font-bold text-3xl capitalize text-primary-500 tracking-tight">
                {{ notice.title }}
            </h1>
        </div>
        }

        <!-- Image Banner Row -->
        @if (notice.imageUrl) {
        <div class="w-full my-4">
            <div class="w-full overflow-hidden shadow-sm max-h-[350px] bg-gray-200">
                <img [src]="notice.imageUrl" class="w-full h-full object-cover object-center" />
            </div>
        </div>
        }

        <!-- Details Row (Metadata & Description) -->
        <div class="flex flex-col gap-4 w-full mt-2">
            <div class="flex items-center gap-3 text-sm text-blueGray-500">
                <time-before size="lg" class="flex items-center text-lg"
                    [dateValue]="notice.publishOn.toDate()"></time-before>
                @if (notice.isGradeNotice) {
                <span class="h-1.5 w-1.5 bg-gray-300 rounded-full"></span>
                <span class="text-start bg-primary-50 text-primary-700 text-lg font-semibold rounded-full">
                    {{ notice.gradeName }}
                </span>
                }
            </div>

            <p class="text-blueGray-700 text-base whitespace-pre-line leading-relaxed">
                {{ notice.description }}
            </p>
        </div>

        <!-- Attachments Row -->
        @if (notice.attachments && notice.attachments.length > 0) {
        <div class="w-full mt-2">
            <h3 class="text-lg font-bold text-blueGray-800 mb-3">Attachments</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                @for (attachment of notice.attachments; track attachment.name) {
                <a [href]="attachment.url" target="_blank"
                    class="flex items-center gap-3 p-3 bg-blueGray-50 border border-blueGray-200 hover:border-primary-500 rounded-lg shadow-sm transition-all duration-150">
                    <i [class]="attachment.url.toLowerCase().endsWith('.pdf') ? 'far fa-file-pdf text-primary-500 text-lg' : 'far fa-file-lines text-primary-500 text-lg'"></i>
                    <span class="text-sm font-semibold text-blueGray-700 truncate" [title]="attachment.name">
                        {{ attachment.name }}
                    </span>
                </a>
                }
            </div>
        </div>
        }

        <!-- Sub-Events Table Row -->
        @if (hasEvents(notice.events)) {
        <div class="w-full mt-2">
            <h3 class="text-lg font-bold text-blueGray-800 mb-3">Events</h3>
            <div class="overflow-hidden rounded-xl border border-blueGray-200 shadow-sm bg-white w-full">
                <table class="w-full text-left border-collapse">
                    <thead>
                        <tr class="bg-blueGray-50 border-b border-blueGray-200">
                            <th class="px-4 py-3 text-xs font-bold uppercase tracking-wider text-blueGray-500">
                                Event / Session Name
                            </th>
                            <th
                                class="px-4 py-3 text-xs font-bold uppercase tracking-wider text-blueGray-500 text-right">
                                Date & Time
                            </th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-blueGray-150">
                        @for (ev of getSortedEvents(notice.events); track ev.name) {
                        <tr class="hover:bg-blueGray-50/30 transition-colors">
                            <td class="px-4 py-3 text-sm font-semibold text-blueGray-800">
                                {{ ev.name }}
                            </td>
                            <td class="px-4 py-3 text-sm text-blueGray-600 text-right font-medium">
                                {{ ev.date.toDate() | moment: 'Do MMM YYYY, h:mm a' }}
                            </td>
                        </tr>
                        }
                    </tbody>
                </table>
            </div>
        </div>
        }

    </div>
    }

    <!-- Not found State -->
    @if (!loading && !notice) {
    <div class="p-12 text-center text-blueGray-400">
        <div class="text-4xl mb-3">⚠️</div>
        <p class="text-lg font-semibold">Notice Not Found</p>
        <p class="text-sm mt-1">This notice may have been deleted or expired.</p>
    </div>
    }
</ng-template>